/**
 * Typed example questions for the chat-button entry (05-einstiege.md section 2,
 * prototype `proto.js` "rotating typed placeholder").
 *
 * The idle label stands for `startDelayMs`, is deleted character by character,
 * then the next example question is typed (55-105 ms per character), held,
 * deleted, a short pause, the next one -- forever. The caret blink is CSS.
 *
 * Two layers: `stepTyping()` is the pure state machine (no DOM, no timers), and
 * `createTypingLoop()` drives it through an injected clock, so both are tested
 * without fake DOM timers.
 */

export interface TypingTiming {
	/** How long the idle label stands before the first deletion. */
	startDelayMs: number;
	/** Per character while deleting. */
	deleteCharMs: number;
	/** Per character while typing: uniform in [min, max). */
	typeCharMinMs: number;
	typeCharMaxMs: number;
	/** A fully typed question stays this long. */
	holdMs: number;
	/** Empty field before the next question starts. */
	pauseMs: number;
}

export const TYPING_TIMING: Readonly< TypingTiming > = {
	startDelayMs: 2600,
	deleteCharMs: 20,
	typeCharMinMs: 55,
	typeCharMaxMs: 105,
	holdMs: 2400,
	pauseMs: 380,
};

export type TypingPhase = 'start' | 'deleting' | 'typing' | 'holding';

export interface TypingState {
	phase: TypingPhase;
	/** The text being shown or typed, as code points (no split surrogates). */
	target: string[];
	/** How many code points of `target` are visible. */
	shown: number;
	/** Index of the next example question. */
	next: number;
}

export interface TypingStep {
	state: TypingState;
	/** The text to render now. */
	text: string;
	/** Characters are moving: the caret should not blink. */
	typing: boolean;
	/** Delay until the next step. */
	delayMs: number;
}

/**
 * The state before the first step: the idle label fully visible.
 * @param idle The static label.
 */
export function initialTypingState( idle: string ): TypingState {
	const target = Array.from( idle );
	return { phase: 'start', target, shown: target.length, next: 0 };
}

/**
 * Advance one step. Pure: `random` is injected (returns [0, 1)).
 * @param state    Current state.
 * @param examples Example questions; must not be empty.
 * @param random   Random source.
 * @param timing   Timing constants.
 */
export function stepTyping(
	state: TypingState,
	examples: readonly string[],
	random: () => number = Math.random,
	timing: TypingTiming = TYPING_TIMING
): TypingStep {
	if ( state.phase === 'typing' ) {
		const shown = Math.min( state.shown + 1, state.target.length );
		if ( shown < state.target.length ) {
			const span = timing.typeCharMaxMs - timing.typeCharMinMs;
			const r = Math.min( Math.max( random(), 0 ), 1 );
			const next: TypingState = { ...state, shown };
			return {
				state: next,
				text: state.target.slice( 0, shown ).join( '' ),
				typing: true,
				delayMs: timing.typeCharMinMs + r * span,
			};
		}
		return {
			state: { ...state, phase: 'holding', shown },
			text: state.target.join( '' ),
			typing: false,
			delayMs: timing.holdMs,
		};
	}

	// start, holding, deleting: remove one character.
	const shown = Math.max( state.shown - 1, 0 );
	if ( shown > 0 ) {
		return {
			state: { ...state, phase: 'deleting', shown },
			text: state.target.slice( 0, shown ).join( '' ),
			typing: true,
			delayMs: timing.deleteCharMs,
		};
	}
	// Empty: pick the next question, type it after the pause.
	const index = examples.length ? state.next % examples.length : 0;
	return {
		state: {
			phase: 'typing',
			target: Array.from( examples[ index ] ?? '' ),
			shown: 0,
			next: ( index + 1 ) % Math.max( examples.length, 1 ),
		},
		text: '',
		typing: false,
		delayMs: timing.pauseMs,
	};
}

/** The clock the loop runs on; `window` timers in the browser, a fake in tests. */
export interface TypingClock {
	now: () => number;
	setTimeout: ( run: () => void, ms: number ) => unknown;
	clearTimeout: ( handle: unknown ) => void;
}

export interface TypingLoopOptions {
	idle: string;
	examples: readonly string[];
	/** Paint a step. */
	render: ( text: string, typing: boolean ) => void;
	clock: TypingClock;
	random?: () => number;
	timing?: TypingTiming;
}

export interface TypingLoop {
	/** Stop moving; the visible text stays. Idempotent. */
	pause: () => void;
	/** Continue with the delay that was left when it paused. Idempotent. */
	resume: () => void;
	/** Stop for good and show the idle label again. */
	stop: () => void;
	readonly running: boolean;
}

/**
 * Drive `stepTyping()` on a clock. Starts paused; call `resume()` once the
 * element is visible. Pausing keeps the remaining delay of the current step.
 * Without examples nothing ever moves.
 * @param options
 */
export function createTypingLoop( options: TypingLoopOptions ): TypingLoop {
	const { idle, examples, render, clock } = options;
	const random = options.random ?? Math.random;
	const timing = options.timing ?? TYPING_TIMING;
	let state = initialTypingState( idle );
	let remaining = timing.startDelayMs;
	let dueAt = 0;
	let handle: unknown = null;
	let running = false;
	let stopped = false;

	const schedule = ( ms: number ): void => {
		dueAt = clock.now() + ms;
		handle = clock.setTimeout( tick, ms );
	};

	function tick(): void {
		handle = null;
		if ( ! running ) {
			return;
		}
		const step = stepTyping( state, examples, random, timing );
		state = step.state;
		render( step.text, step.typing );
		schedule( step.delayMs );
	}

	const pause = (): void => {
		if ( ! running ) {
			return;
		}
		running = false;
		if ( handle !== null ) {
			clock.clearTimeout( handle );
			handle = null;
			remaining = Math.max( 0, dueAt - clock.now() );
		}
	};

	return {
		pause,
		resume() {
			if ( running || stopped || ! examples.length ) {
				return;
			}
			running = true;
			schedule( remaining );
		},
		stop() {
			pause();
			stopped = true;
			state = initialTypingState( idle );
			render( idle, false );
		},
		get running() {
			return running;
		},
	};
}

/**
 * Example questions from a `data-dhsv-chat-examples` attribute: a JSON array
 * of non-empty strings. Anything else yields an empty list.
 * @param value Raw attribute value.
 */
export function parseExamplesAttribute( value: string | null ): string[] {
	if ( ! value ) {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse( value );
		if ( ! Array.isArray( parsed ) ) {
			return [];
		}
		return parsed
			.filter( ( q ): q is string => typeof q === 'string' )
			.map( ( q ) => q.trim() )
			.filter( ( q ) => q !== '' );
	} catch {
		return [];
	}
}
